import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Activity, User, Users, Droplet, ScanLine } from "lucide-react";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UnifiedDisplay() {
  const [departments, setDepartments] = useState({
    registration: null,
    physical_exam_male: null,
    physical_exam_female: null,
    blood_collection: null,
    xray: null
  });
  const [nextTokens, setNextTokens] = useState({});
  const lastAnnouncedRef = useRef({});
  const { speakWithGenderVoices } = useSpeechSynthesis();

  const fetchAllQueues = async () => {
    try {
      // Fetch queues and tokens based on the correct flow:
      // Registration (issued) -> Blood (registered) -> Physical (blood_done) -> X-Ray (physical_done)
      const [reg, physMale, physFemale, blood, xray, 
             issuedTokens, registeredTokens, 
             bloodDoneMale, bloodDoneFemale,
             physicalDoneMale, physicalDoneFemale] = await Promise.all([
        axios.get(`${API}/departments/registration/queue`),
        axios.get(`${API}/departments/physical_exam_male/queue`),
        axios.get(`${API}/departments/physical_exam_female/queue`),
        axios.get(`${API}/departments/blood_collection/queue`),
        axios.get(`${API}/departments/xray/queue`),
        // For Registration: next from issued
        axios.get(`${API}/tokens?status=issued`),
        // For Blood Collection: next from registered
        axios.get(`${API}/tokens?status=registered`),
        // For Physical Exam: next from blood_done
        axios.get(`${API}/tokens?status=blood_done&gender=Male`),
        axios.get(`${API}/tokens?status=blood_done&gender=Female`),
        // For X-Ray: next from physical_done
        axios.get(`${API}/tokens?status=physical_done&gender=Male`),
        axios.get(`${API}/tokens?status=physical_done&gender=Female`)
      ]);

      const newDepts = {
        registration: reg.data,
        physical_exam_male: physMale.data,
        physical_exam_female: physFemale.data,
        blood_collection: blood.data,
        xray: xray.data
      };

      // Sort tokens by token number for correct ordering
      const sortByTokenNumber = (tokens) => [...tokens].sort((a, b) => parseInt(a.token_number) - parseInt(b.token_number));

      // Calculate next tokens based on the correct flow
      const newNextTokens = {
        // Registration: next token from "issued" status
        registration: sortByTokenNumber(issuedTokens.data)
          .find(t => t.token_number !== newDepts.registration?.current_token)?.token_number || null,
        
        // Physical Exam Male: next token from "blood_done" status (after blood collection)
        physical_exam_male: sortByTokenNumber(bloodDoneMale.data)
          .find(t => t.token_number !== newDepts.physical_exam_male?.current_token)?.token_number || null,
        
        // Physical Exam Female: next token from "blood_done" status (after blood collection)
        physical_exam_female: sortByTokenNumber(bloodDoneFemale.data)
          .find(t => t.token_number !== newDepts.physical_exam_female?.current_token)?.token_number || null,
        
        // Blood Collection: next token from "registered" status (after registration)
        blood_collection: sortByTokenNumber(registeredTokens.data)
          .find(t => t.token_number !== newDepts.blood_collection?.current_token)?.token_number || null,
        
        // X-Ray: next token from "physical_done" status (after physical exam)
        xray: sortByTokenNumber(physicalDoneMale.data)
          .find(t => t.token_number !== newDepts.xray?.current_token)?.token_number 
          || sortByTokenNumber(physicalDoneFemale.data)
          .find(t => t.token_number !== newDepts.xray?.current_token)?.token_number || null
      };

      // Check for new tokens and announce them ONLY if they changed
      Object.keys(newDepts).forEach(dept => {
        const currentToken = newDepts[dept]?.current_token;
        if (currentToken && currentToken !== lastAnnouncedRef.current[dept]) {
          announceToken(currentToken, dept);
          lastAnnouncedRef.current[dept] = currentToken;
        }
      });

      setDepartments(newDepts);
      setNextTokens(newNextTokens);
    } catch (error) {
      console.error("Failed to fetch queues", error);
    }
  };

  const announceToken = (tokenNumber, department) => {
    const deptNames = {
      registration: "Registration Desk",
      physical_exam_male: "Physical Examination for Male patients",
      physical_exam_female: "Physical Examination for Female patients",
      blood_collection: "Blood Collection Department",
      xray: "X-Ray Department"
    };

    const message = `Token ${tokenNumber}, please proceed to ${deptNames[department]}`;
    speakWithGenderVoices(message);
  };

  useEffect(() => {
    fetchAllQueues();
    const interval = setInterval(fetchAllQueues, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white p-8" data-testid="unified-display">
      <div className="max-w-[1800px] mx-auto space-y-8">
        <div className="text-center border-b-4 border-slate-900 pb-6">
          <div className="flex items-center justify-center gap-4 mb-3">
            <Activity className="h-14 w-14 text-slate-900" />
            <h1 className="text-5xl font-bold tracking-tight text-slate-900">Richmond Medicare</h1>
          </div>
          <p className="text-2xl font-semibold text-slate-600">Token Display - All Departments</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Registration */}
          <div className="bg-slate-900 text-white rounded-xl p-8 shadow-xl" data-testid="dept-registration">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Registration</h2>
            </div>
            <div className="text-center mt-6">
              <div className="text-sm opacity-80 mb-2">NOW SERVING</div>
              <div className="font-mono text-6xl font-black tracking-wider">
                {departments.registration?.current_token || "---"}
              </div>
              {nextTokens.registration && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="text-xs opacity-60 mb-1">NEXT - GET READY</div>
                  <div className="font-mono text-3xl font-bold opacity-80">
                    {nextTokens.registration}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Physical Exam Male */}
          <div className="bg-indigo-600 text-white rounded-xl p-8 shadow-xl" data-testid="dept-physical-male">
            <div className="flex items-center gap-3 mb-4">
              <User className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Physical (Male)</h2>
            </div>
            <div className="text-center mt-6">
              <div className="text-sm opacity-80 mb-2">NOW SERVING</div>
              <div className="font-mono text-6xl font-black tracking-wider">
                {departments.physical_exam_male?.current_token || "---"}
              </div>
              {nextTokens.physical_exam_male && (
                <div className="mt-4 pt-4 border-t border-indigo-500">
                  <div className="text-xs opacity-60 mb-1">NEXT - GET READY</div>
                  <div className="font-mono text-3xl font-bold opacity-80">
                    {nextTokens.physical_exam_male}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Physical Exam Female */}
          <div className="bg-rose-600 text-white rounded-xl p-8 shadow-xl" data-testid="dept-physical-female">
            <div className="flex items-center gap-3 mb-4">
              <Users className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Physical (Female)</h2>
            </div>
            <div className="text-center mt-6">
              <div className="text-sm opacity-80 mb-2">NOW SERVING</div>
              <div className="font-mono text-6xl font-black tracking-wider">
                {departments.physical_exam_female?.current_token || "---"}
              </div>
              {nextTokens.physical_exam_female && (
                <div className="mt-4 pt-4 border-t border-rose-500">
                  <div className="text-xs opacity-60 mb-1">NEXT - GET READY</div>
                  <div className="font-mono text-3xl font-bold opacity-80">
                    {nextTokens.physical_exam_female}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Blood Collection */}
          <div className="bg-red-600 text-white rounded-xl p-8 shadow-xl" data-testid="dept-blood">
            <div className="flex items-center gap-3 mb-4">
              <Droplet className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Blood Collection</h2>
            </div>
            <div className="text-center mt-6">
              <div className="text-sm opacity-80 mb-2">NOW SERVING</div>
              <div className="font-mono text-6xl font-black tracking-wider">
                {departments.blood_collection?.current_token || "---"}
              </div>
              {nextTokens.blood_collection && (
                <div className="mt-4 pt-4 border-t border-red-500">
                  <div className="text-xs opacity-60 mb-1">NEXT - GET READY</div>
                  <div className="font-mono text-3xl font-bold opacity-80">
                    {nextTokens.blood_collection}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* X-Ray */}
          <div className="bg-purple-600 text-white rounded-xl p-8 shadow-xl" data-testid="dept-xray">
            <div className="flex items-center gap-3 mb-4">
              <ScanLine className="h-8 w-8" />
              <h2 className="text-2xl font-bold">X-Ray</h2>
            </div>
            <div className="text-center mt-6">
              <div className="text-sm opacity-80 mb-2">NOW SERVING</div>
              <div className="font-mono text-6xl font-black tracking-wider">
                {departments.xray?.current_token || "---"}
              </div>
              {nextTokens.xray && (
                <div className="mt-4 pt-4 border-t border-purple-500">
                  <div className="text-xs opacity-60 mb-1">NEXT - GET READY</div>
                  <div className="font-mono text-3xl font-bold opacity-80">
                    {nextTokens.xray}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* System Status */}
          <div className="bg-slate-100 border-4 border-slate-300 rounded-xl p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="text-slate-600 text-lg mb-2">System Active</div>
              <div className="text-slate-500 text-sm animate-pulse-slow">Auto-updating every 3 seconds</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}